/**
 * The small multiples (PRD 10.3, V-5; Phase 25): the heatmap as a grid of sparklines, one per
 * row, every panel on the same y-scale with the same threshold bands behind it.
 *
 * The heatmap answers *which week hurts* and the bullet rows answer *by how many hours*; the
 * small multiples answer *what shape* — a ramp toward overload and a one-week spike look
 * identical as coloured cells and completely different as lines. Sorting by peak puts the rows
 * that need a decision at the top of the grid.
 *
 * They are the same numbers as the matrix: every point is a field of the `GET /forecast/heatmap`
 * cell for that row and week (Phase 25's gate is exactly that comparison, week by week). The
 * shared y-scale is the whole point of the view — panels are only comparable if a height means
 * the same allocation in every one of them — so the scale is computed here once for the grid,
 * never per panel.
 *
 * Shared by the browser client and the tests, as `heatmap.js` and `bullet.js` are. Pure: no DOM,
 * no I/O.
 */
import { STATUS_STATES } from './palette.js';
import { col } from './table.js';
import { ROW_NOUNS } from './heatmap.js';
import { round2, thresholdScale } from './heatmap.js';
import { ticks } from './scales.js';

/** The orders the card offers. `peak` is the default the plan asks for: the worst row first. */
export const MULTIPLE_SORTS = Object.freeze(['peak', 'average', 'label']);

/**
 * The two shared scales. `fit` runs to the grid's own worst peak, which is the honest reading;
 * `focus` runs to a fixed ceiling and clips above it, for a grid where one row is several times
 * every other and would otherwise flatten them all into the floor. Both are shared across every
 * panel — the requirement is one scale for the grid, not one that never clips.
 */
export const SCALE_MODES = Object.freeze(['fit', 'focus']);

/** Where `focus` tops out: twice capacity, or just past the overloaded threshold if that is higher. */
export const FOCUS_CEILING = 2;

/** The coordinate space a panel is drawn in. The SVG scales; the arithmetic does not. */
export const PANEL = Object.freeze({ width: 120, height: 40 });

/**
 * How much taller than the worst peak the `fit` scale runs, so the highest point is a point on
 * the line rather than a dot welded to the top edge of its panel.
 */
export const SCALE_HEADROOM = 1.06;

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const hours = (v) => round2(num(v) ?? 0);

/** One point of a sparkline: the week's cell, and what the line does with it. */
function pointFrom(cell, index, thresholds) {
  const utilization = num(cell.utilization);
  const capacity = hours(cell.capacity_hours);
  return {
    index,
    week_start: cell.week_start,
    week_end: cell.week_end ?? null,
    utilization,
    allocated_hours: hours(cell.allocated_hours),
    capacity_hours: capacity,
    high_confidence_hours: hours(cell.high_confidence_hours),
    headroom_hours: num(cell.headroom_hours),
    band: cell.band ?? null,
    has_capacity: capacity > 0,
    // The dot the plan asks for: a week past the overloaded threshold, not merely over capacity.
    over: utilization !== null && utilization > thresholds.overloaded_above,
    cell,
  };
}

/** The peak point of a series (ties to the earlier week); null when no week has capacity. */
export function peakPoint(points) {
  let best = null;
  for (const p of points ?? []) {
    if (p.utilization === null) continue;
    if (best === null || p.utilization > best.utilization) best = p;
  }
  return best;
}

/**
 * A series's average allocation, weighted by capacity — the same rule the heatmap's row average
 * uses, so the two cards order rows identically. Null when no week has capacity.
 */
export function averageOf(points) {
  let demand = 0;
  let capacity = 0;
  for (const p of points ?? []) {
    if (!p.has_capacity) continue;
    demand += p.allocated_hours;
    capacity += p.capacity_hours;
  }
  return capacity > 0 ? Math.round((demand / capacity) * 10000) / 10000 : null;
}

/** One panel of the grid from a heatmap row. */
function seriesFrom(source, cells, thresholds, { reference = false } = {}) {
  const points = (cells ?? []).map((c, i) => pointFrom(c, i, thresholds));
  const peak = peakPoint(points);
  return {
    key: source.key,
    label: source.label,
    entity: source.entity ?? null,
    reference,
    points,
    peak,
    peak_utilization: peak?.utilization ?? null,
    peak_week: peak?.week_start ?? null,
    average: averageOf(points),
    weeks: points.length,
    weeks_over: points.filter((p) => p.over).length,
    weeks_without_capacity: points.filter((p) => !p.has_capacity).length,
    has_capacity: points.some((p) => p.has_capacity),
    allocated_hours: round2(points.reduce((s, p) => s + p.allocated_hours, 0)),
    capacity_hours: round2(points.reduce((s, p) => s + p.capacity_hours, 0)),
  };
}

/**
 * Order the panels. `peak` and `average` descend with the rows that have no capacity last (they
 * have no ratio to rank), ties by the API's order; `label` is the API's order untouched.
 * Never mutates the input.
 * @param {object[]} series
 * @param {'peak' | 'average' | 'label'} by
 */
export function sortSeries(series, by = 'peak') {
  const list = [...(series ?? [])];
  if (by === 'label') return list;
  const measure = (s) => (by === 'average' ? s.average : s.peak_utilization);
  return list
    .map((s, i) => ({ s, i, m: s.has_capacity ? measure(s) : null }))
    .sort((a, b) => {
      if (a.m === null && b.m === null) return a.i - b.i;
      if (a.m === null) return 1;
      if (b.m === null) return -1;
      return b.m - a.m || a.i - b.i;
    })
    .map((x) => x.s);
}

/**
 * The one y-scale every panel is drawn on, as an allocation ratio.
 *
 * It is measured on the entity panels alone, for the reason Phase 24's axis was: the company
 * reference is a sum of them and on the dev database it reads 543% in a week where the worst
 * person reads 276%. A panel whose line runs past the top is clipped and marked, never quietly
 * flattened — and the `focus` mode exists for the grid where that is the normal case.
 *
 * @param {object[]} series  the entity panels (not the company reference)
 * @param {object} thresholds
 * @param {'fit' | 'focus'} mode
 */
export function multiplesScale(series, thresholds, mode = 'fit') {
  const ceiling = Math.max(FOCUS_CEILING, round2(thresholds.overloaded_above * 1.1));
  if (mode === 'focus') {
    const t = ticks([0, ceiling], { count: 4 });
    return { mode, max: t.domain[1], step: t.step, values: t.values };
  }
  const peaks = series.filter((s) => s.has_capacity).map((s) => s.peak_utilization);
  // With nothing staffed anywhere there is no ratio to measure, so the scale is the focus one:
  // the bands still mean something, and every panel is empty in the same way.
  const worst = peaks.length ? Math.max(...peaks) : 0;
  const t = ticks([0, Math.max(ceiling, worst * SCALE_HEADROOM)], { count: 4 });
  return { mode, max: t.domain[1], step: t.step, values: t.values };
}

/** The share of the scale a panel's whole line has to live in before it is squashed flat. */
export const FLATTENED_BELOW = 0.25;

/**
 * What a shared scale costs the panels that are nowhere near it: how many staffed panels have
 * their entire line inside the bottom quarter of it, where a ramp and a spike look the same.
 *
 * This is the price of the requirement, not a fault in it, and the card states it rather than
 * hiding it: on the dev database the slip pile-up puts one service at 842%, so the `fit` scale
 * runs to 1000% and every panel under 250% is a flat line along the floor. The figure is what
 * the note points at the `focus` scale with.
 */
function withFlattened(scale, series) {
  const staffed = series.filter((s) => s.has_capacity);
  return {
    ...scale,
    staffed: staffed.length,
    flattened: staffed.filter((s) => s.peak_utilization < scale.max * FLATTENED_BELOW).length,
  };
}

/**
 * Everything the grid draws: the panels, the company reference panel, the weeks along the
 * bottom, and the shared scale they are all measured on.
 *
 * @param {object} data  a `GET /forecast/heatmap` response
 * @param {{ sort?: 'peak'|'average'|'label', scale?: 'fit'|'focus' }} [opts]
 */
export function multiplesModel(data, { sort = 'peak', scale = 'fit' } = {}) {
  const weeks = (data?.totals ?? []).map((t) => ({
    week_start: t.week_start,
    week_end: t.week_end ?? null,
    utilization: num(t.utilization),
    band: t.band ?? null,
  }));
  if (!weeks.length) return null;
  const thresholds = data.thresholds ?? {
    underload_below: 0.85,
    caution_above: 1,
    overloaded_above: 1.15,
  };
  const series = (data.rows ?? []).map((row) => seriesFrom(row, row.cells, thresholds));
  const sorted = sortSeries(series, sort);
  // The company total is on the same ratio scale as the rows, unlike Phase 24's hours, so it can be
  // a panel — as the reference the others are read against. In the company dimension the one
  // row already is the company and a second panel would just repeat it.
  const reference =
    data.dimension === 'company'
      ? null
      : seriesFrom({ key: 'company', label: 'Company' }, data.totals, thresholds, {
          reference: true,
        });
  return {
    weeks,
    series: sorted,
    reference,
    scale: withFlattened(multiplesScale(series, thresholds, scale), series),
    thresholds,
    sort,
    dimension: data.dimension,
  };
}

/**
 * The background every panel shares: the threshold bands as horizontal tints, and the rules at
 * capacity and at the overloaded threshold. Identical in every panel by construction, which is
 * what lets the reader compare shapes across the grid.
 * @param {{ max: number }} scale
 * @param {object} thresholds
 */
export function multiplesBackground(scale, thresholds) {
  const states = thresholdScale(thresholds);
  const bands = [];
  let from = 0;
  for (const state of states) {
    const to = state.max === null ? scale.max : Math.min(scale.max, state.max);
    if (to > from) {
      bands.push({
        key: state.key,
        // SVG y grows downward: the top of a band is the larger value.
        y: round2(PANEL.height - (to / scale.max) * PANEL.height),
        height: round2(((to - from) / scale.max) * PANEL.height),
        from,
        to,
      });
    }
    from = to;
    if (from >= scale.max) break;
  }
  const ruleAt = (value) => round2(PANEL.height - (value / scale.max) * PANEL.height);
  const rules = [{ key: 'capacity', value: thresholds.caution_above, y: ruleAt(1) }];
  if (thresholds.overloaded_above < scale.max)
    rules.push({
      key: 'overloaded',
      value: thresholds.overloaded_above,
      y: ruleAt(thresholds.overloaded_above),
    });
  return { bands, rules };
}

/** Where a week sits across a panel. One week alone is drawn in the middle, not at the edge. */
export function weekX(index, count) {
  if (count <= 1) return round2(PANEL.width / 2);
  return round2((index / (count - 1)) * PANEL.width);
}

/**
 * A panel's marks in the panel coordinate space: the line (broken wherever a week has no
 * capacity and therefore no ratio), the points, the overload dots, and the weeks with no
 * capacity marked on the floor.
 *
 * A point past the top of the shared scale is clamped to the top and flagged, so a clipped line
 * is drawn as a line that reaches the ceiling and is marked, never as one that quietly stops.
 *
 * @param {ReturnType<typeof multiplesModel>['series'][number]} series
 * @param {{ max: number }} scale
 */
export function multiplesGeometry(series, scale) {
  const count = series.points.length;
  const max = scale.max > 0 ? scale.max : 1;
  const points = series.points.map((p) => {
    const x = weekX(p.index, count);
    if (p.utilization === null) return { ...p, x, y: null, clipped: false };
    const clipped = p.utilization > max;
    const value = Math.min(p.utilization, max);
    return { ...p, x, y: round2(PANEL.height - (value / max) * PANEL.height), clipped };
  });
  // One path per run of consecutive weeks with capacity; a single such week is a dot, not a path.
  const segments = [];
  let run = [];
  const flush = () => {
    if (run.length > 1)
      segments.push(run.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x} ${p.y}`).join(' '));
    run = [];
  };
  for (const p of points) {
    if (p.y === null) flush();
    else run.push(p);
  }
  flush();
  return {
    points,
    segments,
    dots: points.filter((p) => p.over && p.y !== null),
    // A week with no capacity: no ratio, so no line. Marked on the floor with the hours that
    // still landed there, which is the matrix's hatched cell said in one mark.
    gaps: points.filter((p) => p.y === null),
    clipped: points.some((p) => p.clipped),
    // A lone week with capacity has no line to draw; the card draws its point instead.
    lone: segments.length === 0 && points.some((p) => p.y !== null),
  };
}

/** The figures the card's header states for the grid. */
export function multiplesSummary(model) {
  const perBand = Object.fromEntries(STATUS_STATES.map((s) => [s.key, 0]));
  let over = 0;
  let noCapacity = 0;
  let worst = null;
  for (const s of model?.series ?? []) {
    if (!s.has_capacity) noCapacity += 1;
    if (s.weeks_over > 0) over += 1;
    for (const p of s.points) if (p.band) perBand[p.band] += 1;
    if (s.peak && (worst === null || s.peak.utilization > worst.peak.utilization))
      worst = { series: s, peak: s.peak };
  }
  return {
    rows: model?.series?.length ?? 0,
    rows_over: over,
    no_capacity: noCapacity,
    per_band: perBand,
    weeks: model?.weeks?.length ?? 0,
    worst,
  };
}

/**
 * The table view's rows: one per panel and week, long format, the same numbers the points are
 * drawn from — so the gate's week-by-week comparison can be done from the export.
 */
export function multiplesExportRows(model) {
  const all = [...(model?.series ?? [])];
  if (model?.reference) all.push(model.reference);
  const out = [];
  for (const s of all) {
    for (const p of s.points) {
      out.push({
        row: s.label,
        key: s.key,
        week_start: p.week_start,
        utilization: p.utilization,
        band: p.band,
        allocated_hours: p.allocated_hours,
        capacity_hours: p.capacity_hours,
        headroom_hours: p.headroom_hours,
        high_confidence_hours: p.high_confidence_hours,
        over: p.over,
        peak_utilization: s.peak_utilization,
        peak_week: s.peak_week,
        average_utilization: s.average,
      });
    }
  }
  return out;
}

// ---------------------------------------------------------------------------------------------
// Table columns (Phase 37)
//
// A specification, not a rendering: the screen decorates it with `withFormats` and the CSV
// writer reads the same list, so an export cannot hold a different set of columns from the
// table it came from.

/** @param {{ dimension?: string }} opts */
export function multiplesColumns({ dimension = 'company' } = {}) {
  return [
    col.text('row', (ROW_NOUNS[dimension] ?? ROW_NOUNS.company)[0]),
    col.week('week_start', 'Week', { type: 'text' }),
    col.percent('utilization', 'Allocation'),
    col.text('band', 'State', { csv: (v) => v ?? '' }),
    col.hours('allocated_hours', 'Allocated'),
    col.hours('capacity_hours', 'Capacity'),
    col.hours('headroom_hours', 'Headroom'),
    col.hours('high_confidence_hours', 'High confidence'),
    col.flag('over', 'Overload dot', { csv: (v) => (v ? 'true' : 'false') }),
    col.percent('peak_utilization', 'Row peak'),
    col.week('peak_week', 'Peak week', { type: 'text' }),
    col.percent('average_utilization', 'Row average'),
  ];
}
